// In-game feedback for testers (spec 2026-10-02 §3.3): the text and the current screen go to the server.
import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useLocation } from 'react-router';
import { endpoints } from '../api/endpoints';
import { useLastScreen } from '../state/lastScreen';
import { Button, ErrorNotice, inputFrame, Notice, Sheet } from './ui';

const MAX = 2000;

export function FeedbackSheet({
  open,
  onClose,
  galaxyId,
}: {
  open: boolean;
  onClose(): void;
  galaxyId: string;
}) {
  const { pathname } = useLocation();
  const screen = useLastScreen((s) => s.byGalaxy[galaxyId]) ?? pathname;
  const id = useId();
  const [message, setMessage] = useState('');
  const send = useMutation({
    mutationFn: () => endpoints.sendFeedback({ galaxyId, screen, message }),
    onSuccess: () => setMessage(''),
  });
  const close = () => {
    send.reset();
    onClose();
  };
  return (
    <Sheet open={open} title="Feedback" onClose={close}>
      {send.isSuccess ? (
        <div className="space-y-3">
          <Notice tone="success">Thanks — your feedback was sent.</Notice>
          <Button className="w-full" onClick={close}>
            Close
          </Button>
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            send.mutate();
          }}
        >
          <label
            htmlFor={id}
            className="block font-display text-xs font-semibold uppercase tracking-[0.14em] text-muted"
          >
            Your feedback
          </label>
          <textarea
            id={id}
            required
            maxLength={MAX}
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className={`${inputFrame} py-2`}
            placeholder="What was confusing, broken or fun?"
          />
          <p className="text-xs text-faint">
            {message.length} / {MAX} · the current screen is sent along.
          </p>
          {send.error ? <ErrorNotice error={send.error} /> : null}
          <Button type="submit" className="w-full" disabled={send.isPending || !message.trim()}>
            Send
          </Button>
        </form>
      )}
    </Sheet>
  );
}
